/**
 * Build-time defaults for Social Scavenger whitelabels.
 *
 * Keyed by `REACT_APP_WHITELABEL`. The `show*` booleans are defaults.
 * Optimizely overrides them when the matching flag exists; audiences use the
 * `whitelabel` attribute. Logo choice, copyright, theme, and image files stay
 * local. They are not experiments.
 */

/**
 * Optimizely flag keys for the product toggles. Create each flag in
 * Optimizely and target it with the user attribute `whitelabel`.
 */
export const WHITELABEL_FLAG_KEYS = {
  showPaymentTab: 'show-payment-tab',
  showReelsLiberate: 'show-reels-liberate',
  showFooterSocial: 'show-footer-social',
  showNewGameButton: 'show-new-game-button',
  showAppPreview: 'show-app-preview',
  showDonationsButton: 'show-donations-button',
  showReelsButton: 'show-reels-button',
}

export const DEFAULT_WHITELABEL_ID = 'ss'

const SOCIAL_SCAVENGER_FEATURES = {
  showPaymentTab: true,
  showReelsLiberate: true,
  showFooterSocial: true,
  showNewGameButton: true,
  showAppPreview: true,
  showDonationsButton: true,
  showReelsButton: true,
  useBrandLogo: false,
  footerCopyright: 'Social Scavenger Inc.',
}

/**
 * Local defaults per whitelabel id. `birthday` matches Social Scavenger
 * product behavior and renders `logo.png`. `eventfabula` matches that
 * product behavior, uses the branch `builder-logo.png` in Header and
 * SideNav, and has its own copyright line. `cityhunt` hides payment,
 * Reels liberation, footer social links, the New Game button, App Preview,
 * the Donations button, and the Reels button until Optimizely says otherwise.
 */
export const WHITELABEL_FEATURES = {
  ss: SOCIAL_SCAVENGER_FEATURES,
  birthday: {
    ...SOCIAL_SCAVENGER_FEATURES,
    useBrandLogo: true,
  },
  eventfabula: {
    ...SOCIAL_SCAVENGER_FEATURES,
    useBrandLogo: false,
    footerCopyright: 'Event Fabula Inc.',
  },
  cityhunt: {
    ...SOCIAL_SCAVENGER_FEATURES,
    showPaymentTab: false,
    showReelsLiberate: false,
    showFooterSocial: false,
    showNewGameButton: false,
    showAppPreview: false,
    showDonationsButton: false,
    showReelsButton: false,
    useBrandLogo: true,
    footerCopyright: 'cityHUNT LLC',
  },
}

/**
 * Resolves the active whitelabel id from `REACT_APP_WHITELABEL`.
 * Unknown or empty values fall back to `ss` so a misconfigured build
 * keeps Social Scavenger product behavior.
 * @returns {string}
 */
export function getWhitelabelId() {
  const raw = (process.env.REACT_APP_WHITELABEL || '').trim().toLowerCase()
  if (raw && Object.prototype.hasOwnProperty.call(WHITELABEL_FEATURES, raw)) {
    return raw
  }
  return DEFAULT_WHITELABEL_ID
}

/**
 * Returns the product-policy flags for the active whitelabel.
 * The year is not included; callers render it next to `footerCopyright`.
 * @returns {{
 *   showPaymentTab: boolean,
 *   showReelsLiberate: boolean,
 *   showFooterSocial: boolean,
 *   showNewGameButton: boolean,
 *   showAppPreview: boolean,
 *   showDonationsButton: boolean,
 *   showReelsButton: boolean,
 *   useBrandLogo: boolean,
 *   footerCopyright: string,
 * }}
 */
export function getWhitelabelFeatures() {
  return WHITELABEL_FEATURES[getWhitelabelId()]
}
